import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { User, Bell, Upload, Eye, Settings, ChevronRight, Check, MessageSquare, Sparkles, Heart, Tag, Reply } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MobileNav from '@/components/MobileNav';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CentrumPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('notifications');

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  }, []);

  const fetchUploads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/my-uploads`);
      setUploads(response.data);
    } catch (error) {
      console.error('Failed to fetch uploads');
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchNotifications(), fetchUploads()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchNotifications, fetchUploads]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('WSZYSTKIE PRZECZYTANE');
    } catch (error) {
      toast.error('BŁĄD');
    }
  };

  const getNotificationIcon = (type) => {
    const iconProps = { className: "w-5 h-5", strokeWidth: 2.5 };
    switch (type) {
      case 'mention': return <Tag {...iconProps} className="w-5 h-5 text-zom-blue" />;
      case 'comment': return <MessageSquare {...iconProps} className="w-5 h-5 text-zom-blue" />;
      case 'reply': return <Reply {...iconProps} className="w-5 h-5 text-zom-blue" />;
      case 'like': return <Heart {...iconProps} className="w-5 h-5 text-zom-red" />;
      case 'inspiration': return <Sparkles {...iconProps} className="w-5 h-5 text-zom-yellow" />;
      case 'admin_message': return <MessageSquare {...iconProps} className="w-5 h-5 text-zom-red" />;
      case 'post_approved': return <Check {...iconProps} className="w-5 h-5 text-zom-yellow" />;
      case 'post_rejected': return <Eye {...iconProps} className="w-5 h-5 text-zom-red" />;
      case 'credit': return <Heart {...iconProps} className="w-5 h-5 text-zom-red" />;
      default: return <Bell {...iconProps} className="w-5 h-5 text-zom-white" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'TERAZ';
    if (diffHours < 24) return `${diffHours}H`;
    if (diffHours < 48) return 'WCZORAJ';
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' }).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PUBLISHED': return 'text-zom-yellow';
      case 'SUBMITTED': return 'text-zom-blue';
      case 'NEEDS_CHANGES': return 'text-zom-red';
      case 'REJECTED': return 'text-zom-red';
      default: return 'text-zom-white';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PUBLISHED': return 'OPUBLIKOWANY';
      case 'SUBMITTED': return 'W KOLEJCE';
      case 'NEEDS_CHANGES': return 'DO POPRAWY';
      case 'REJECTED': return 'ODRZUCONY';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zom-blue flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zom-yellow border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zom-blue text-zom-white pt-20 pb-24">
      {/* Profile header - below top nav */}
      <div className="fixed top-16 left-0 right-0 z-30 bg-zom-black/90 border-b-2 border-zom-yellow p-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-zom-yellow text-zom-black flex items-center justify-center text-xl font-heading border-2 border-zom-black">
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="text-sm font-heading text-zom-yellow">@{user?.email?.split('@')[0]}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="p-4 pt-16">
        <TabsList className="w-full bg-zom-black p-0 border-2 border-zom-white h-auto">
          <TabsTrigger 
            value="notifications" 
            className="flex-1 py-3 font-heading text-xs tracking-wide data-[state=active]:bg-zom-yellow data-[state=active]:text-zom-black"
          >
            <Bell className="w-4 h-4 mr-1" strokeWidth={2.5} />
            POWIAD.
            {unreadCount > 0 && (
              <span className="ml-1 bg-zom-red text-zom-white text-[10px] px-1.5 py-0.5 font-heading">
                {unreadCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="uploads" 
            className="flex-1 py-3 font-heading text-xs tracking-wide data-[state=active]:bg-zom-yellow data-[state=active]:text-zom-black"
          >
            <Upload className="w-4 h-4 mr-1" strokeWidth={2.5} />
            UPLOADY
          </TabsTrigger>
          <TabsTrigger 
            value="profile" 
            className="flex-1 py-3 font-heading text-xs tracking-wide data-[state=active]:bg-zom-yellow data-[state=active]:text-zom-black"
          >
            <User className="w-4 h-4 mr-1" strokeWidth={2.5} />
            PROFIL
          </TabsTrigger>
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-4 space-y-3">
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead} 
              className="w-full py-3 bg-zom-black text-zom-white font-heading text-xs tracking-wide border-2 border-zom-white hover:bg-zom-white hover:text-zom-black transition-colors"
            >
              <Check className="w-4 h-4 inline mr-2" strokeWidth={2.5} />
              OZNACZ WSZYSTKIE
            </button>
          )}

          {notifications.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="w-16 h-16 mx-auto mb-4 text-zom-white/30" strokeWidth={2} />
              <p className="font-heading">BRAK POWIADOMIEŃ</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => {
                  if (!notif.is_read) markAsRead(notif.id);
                  if (notif.link) {
                    // Use React Router for internal navigation
                    navigate(notif.link);
                  }
                }}
                className={`p-4 border-3 cursor-pointer transition-all ${
                  notif.is_read 
                    ? 'bg-zom-black border-zom-white/30' 
                    : 'bg-zom-black border-zom-yellow shadow-brutal'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getNotificationIcon(notif.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-sm">{notif.title}</h3>
                      <span className="text-xs text-zom-white/60 font-heading">{formatDate(notif.created_at)}</span>
                    </div>
                    <p className="text-sm text-zom-white/80 mt-1 font-body">{notif.message}</p>
                  </div>
                  {notif.link && (
                    <ChevronRight className="w-5 h-5 text-zom-white/40" strokeWidth={2.5} />
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Uploads Tab */}
        <TabsContent value="uploads" className="mt-4 space-y-3">
          {uploads.length === 0 ? (
            <div className="text-center py-16">
              <Upload className="w-16 h-16 mx-auto mb-4 text-zom-white/30" strokeWidth={2} />
              <p className="font-heading">BRAK UPLOADÓW</p>
              <button 
                onClick={() => window.location.href = '/upload'}
                className="mt-4 px-6 py-3 bg-zom-yellow text-zom-black font-heading tracking-wide border-3 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow"
              >
                DODAJ PIERWSZY
              </button>
            </div>
          ) : (
            uploads.map((upload) => (
              <div
                key={upload.id}
                className="bg-zom-black border-3 border-zom-white p-4 flex gap-4"
              >
                {/* Thumbnail */}
                <div className="w-20 h-20 bg-zom-black border-2 border-zom-white overflow-hidden flex-shrink-0">
                  {upload.media_type === 'video' ? (
                    <video src={upload.media_url} className="w-full h-full object-cover" />
                  ) : (
                    <img src={upload.media_url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-heading ${getStatusColor(upload.status)}`}>
                      {getStatusLabel(upload.status)}
                    </span>
                  </div>
                  {upload.caption && (
                    <p className="text-sm text-zom-white/70 mt-1 truncate font-body">{upload.caption}</p>
                  )}
                  <p className="text-xs text-zom-white/50 mt-2 font-heading">{formatDate(upload.created_at)}</p>
                  
                  {upload.feedback && (
                    <p className="text-sm text-zom-red mt-2 bg-zom-red/10 p-2 border border-zom-red font-body">
                      FEEDBACK: {upload.feedback}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <div className="bg-zom-black border-3 border-zom-white p-6 space-y-6 shadow-brutal">
            {/* Avatar */}
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 bg-zom-yellow text-zom-black flex items-center justify-center text-4xl font-heading border-4 border-zom-black shadow-brutal-lg">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <h2 className="text-xl font-heading mt-4 tracking-wide">{user?.display_name || user?.email?.split('@')[0]}</h2>
              <p className="text-zom-yellow font-heading">@{user?.email?.split('@')[0]}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zom-blue border-2 border-zom-white p-4 text-center">
                <div className="text-3xl font-heading text-zom-yellow">
                  {uploads.filter(u => u.status === 'PUBLISHED').length}
                </div>
                <div className="text-xs font-heading text-zom-white tracking-wide">OPUBLIKOWANE</div>
              </div>
              <div className="bg-zom-blue border-2 border-zom-white p-4 text-center">
                <div className="text-3xl font-heading text-zom-white">
                  {uploads.filter(u => u.status === 'SUBMITTED').length}
                </div>
                <div className="text-xs font-heading text-zom-white tracking-wide">W KOLEJCE</div>
              </div>
            </div>

            {/* Roles */}
            <div>
              <h3 className="text-xs text-zom-yellow font-heading mb-2 tracking-wide">ROLE</h3>
              <div className="flex flex-wrap gap-2">
                {user?.roles?.map(role => (
                  <span 
                    key={role}
                    className={`px-4 py-2 text-sm font-heading border-2 ${
                      role === 'admin' ? 'bg-zom-red text-zom-white border-zom-black' :
                      role === 'writer' ? 'bg-zom-blue text-zom-white border-zom-white' :
                      role === 'performer' ? 'bg-zom-yellow text-zom-black border-zom-black' :
                      'bg-zom-black text-zom-white border-zom-white'
                    }`}
                  >
                    {role.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>

            {/* Bio */}
            {user?.bio && (
              <div>
                <h3 className="text-xs text-zom-yellow font-heading mb-2 tracking-wide">BIO</h3>
                <p className="text-zom-white font-body">{user.bio}</p>
              </div>
            )}

            <button 
              onClick={() => window.location.href = '/profile'}
              className="w-full py-4 bg-transparent text-zom-white font-heading tracking-wide border-2 border-zom-white hover:bg-zom-white hover:text-zom-black transition-colors"
            >
              <Settings className="w-5 h-5 inline mr-2" strokeWidth={2.5} />
              EDYTUJ PROFIL
            </button>
          </div>
        </TabsContent>
      </Tabs>

      <MobileNav unreadCount={unreadCount} />
    </div>
  );
};

export default CentrumPage;
